import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import {
  platformAdminGetTenants, platformAdminGetTenant, platformAdminCreateTenant,
  platformAdminUpdateTenant, platformAdminUpdateTenantLicense, platformAdminResetTenantAdmin,
  platformAdminLogin, getPlatformToken, clearPlatformToken,
} from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Plus, Pencil, ShieldCheck, AlertTriangle, CheckCircle, Building2, Lock, Copy, RefreshCw, Eye, EyeOff, Mail, Stethoscope } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

// ─── Toast helper (same pattern as SettingsPage) ───────────────────────────────

function useNotify() {
  const [note, setNote] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(null), 3500);
    return () => clearTimeout(t);
  }, [note]);
  return { note, ok: (msg: string) => setNote({ type: 'success', msg }), err: (msg: string) => setNote({ type: 'error', msg }) };
}

function Notify({ note }: { note: { type: 'success' | 'error'; msg: string } | null }) {
  if (!note) return null;
  return (
    <Alert className={cn('mb-4', note.type === 'success' ? 'border-green-200 bg-green-50' : 'border-destructive/30 bg-destructive/5')}>
      {note.type === 'success'
        ? <CheckCircle className="h-4 w-4 text-green-600" />
        : <AlertTriangle className="h-4 w-4 text-destructive" />}
      <AlertDescription className={note.type === 'success' ? 'text-green-700' : 'text-destructive'}>{note.msg}</AlertDescription>
    </Alert>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      {children}
    </div>
  );
}

// ─── Constants ──────────────────────────────────────────────────────────────

const PLANS = ['basic', 'clinic', 'hospital', 'enterprise'] as const;
const STATUSES = ['active', 'suspended', 'cancelled'] as const;

const PLAN_COLORS: Record<string, string> = {
  basic:      'bg-slate-100 text-slate-700',
  clinic:     'bg-blue-100 text-blue-700',
  hospital:   'bg-purple-100 text-purple-700',
  enterprise: 'bg-amber-100 text-amber-700',
};

const STATUS_COLORS: Record<string, string> = {
  active:    'bg-green-100 text-green-700',
  suspended: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-700',
};

type Tenant = {
  id: string;
  name: string;
  subdomain: string | null;
  email: string | null;
  status: string;
  plan: string;
  license_expires_at: string | null;
  max_users: number | null;
  max_clinics: number | null;
  license_features: Record<string, boolean> | null;
  created_at: string;
};

const EMPTY_CREATE_FORM = {
  name: '', subdomain: '', mrn_prefix: '', email: '', phone: '', address: '',
  plan: 'hospital' as typeof PLANS[number],
  max_users: '', max_clinics: '', license_expires_at: '',
  admin_name: '', admin_email: '', admin_password: '',
  admin_is_doctor: false, admin_specialty: '', admin_registration_no: '',
};

// ─── Utilities ────────────────────────────────────────────────────────────────

const CHARSET = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%';
function generatePassword(len = 12): string {
  return Array.from({ length: len }, () => CHARSET[Math.floor(Math.random() * CHARSET.length)]).join('');
}

async function copyToClipboard(text: string) {
  try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
}

function CopyField({ label, value, secret }: { label: string; value: string; secret?: boolean }) {
  const [visible, setVisible] = useState(!secret);
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    await copyToClipboard(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          readOnly
          value={visible ? value : '•'.repeat(Math.min(value.length, 16))}
          className="text-xs font-mono h-8 flex-1"
        />
        {secret && (
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setVisible(v => !v)}>
            {visible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </Button>
        )}
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={handle}>
          {copied ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
        </Button>
      </div>
    </div>
  );
}

// ─── Platform secret challenge gate ───────────────────────────────────────────

function PlatformChallenge({ onUnlocked }: { onUnlocked: () => void }) {
  const { currentUser } = useAuth();
  const [password, setPassword] = useState('');
  const [secret, setSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await platformAdminLogin(currentUser?.email ?? '', password, secret);
      onUnlocked();
    } catch (e: any) {
      setError(e?.message || 'Authentication failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm space-y-6 p-8 rounded-xl border shadow-sm bg-card">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-lg font-semibold">Platform Admin Access</h1>
          <p className="text-sm text-muted-foreground">Re-enter your password and the platform secret to continue.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-sm">Password</Label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Platform Secret</Label>
            <Input type="password" value={secret} onChange={e => setSecret(e.target.value)} required placeholder="X-Platform-Secret" />
          </div>
          {error && (
            <div className="flex items-center gap-2 text-xs text-destructive">
              <AlertTriangle className="w-3.5 h-3.5" />{error}
            </div>
          )}
          <Button type="submit" className="w-full" disabled={saving}>
            {saving ? 'Verifying…' : 'Unlock'}
          </Button>
        </form>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function PlatformAdminPage() {
  const { note, ok, err } = useNotify();
  const [unlocked, setUnlocked] = useState(() => !!getPlatformToken());
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);

  // Create tenant modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState('');

  // Manage tenant modal
  const [manageTenant, setManageTenant] = useState<Tenant | null>(null);
  const [manageForm, setManageForm] = useState({
    name: '', email: '', phone: '', address: '', status: 'active' as typeof STATUSES[number],
    plan: 'hospital' as typeof PLANS[number],
    max_users: '', max_clinics: '', license_expires_at: '',
  });
  const [manageUsage, setManageUsage] = useState<{ clinics: number; doctors: number; patients: number; users: number } | null>(null);
  const [manageSaving, setManageSaving] = useState(false);

  // Reset admin password (inside manage dialog)
  const [resetPassword, setResetPassword] = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [resetResult, setResetResult] = useState<{ adminEmail: string; emailSent: boolean } | null>(null);

  // Credentials modal (shown after tenant creation)
  const [credentials, setCredentials] = useState<{
    tenantName: string; adminName: string; adminEmail: string;
    adminPassword: string; emailScheduled: boolean;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await platformAdminGetTenants();
      setTenants(data ?? []);
    } catch (e: any) {
      if (e?.status === 403 || e?.message?.includes('Platform admin')) {
        clearPlatformToken();
        setUnlocked(false);
      } else {
        err('Failed to load tenants');
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (unlocked) load(); }, [unlocked, load]);

  if (!unlocked) return <PlatformChallenge onUnlocked={() => setUnlocked(true)} />;

  const handleCreate = async () => {
    setCreateError('');
    const f = createForm;
    if (!f.name || !f.mrn_prefix) { setCreateError('Name and MRN prefix are required'); return; }
    if (!f.admin_name || !f.admin_email || !f.admin_password) { setCreateError('Admin name, email, and password are required'); return; }

    setCreateSaving(true);
    try {
      const result = await platformAdminCreateTenant({
        name: f.name,
        subdomain: f.subdomain || undefined,
        mrn_prefix: f.mrn_prefix.toUpperCase(),
        email: f.email || undefined,
        phone: f.phone || undefined,
        address: f.address || undefined,
        plan: f.plan,
        max_users: f.max_users ? Number(f.max_users) : undefined,
        max_clinics: f.max_clinics ? Number(f.max_clinics) : undefined,
        license_expires_at: f.license_expires_at || undefined,
        admin_name: f.admin_name,
        admin_email: f.admin_email,
        admin_password: f.admin_password,
        admin_is_doctor: f.admin_is_doctor || undefined,
        admin_specialty: f.admin_specialty || undefined,
        admin_registration_no: f.admin_registration_no || undefined,
      });
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE_FORM);
      setCredentials({
        tenantName: f.name,
        adminName: f.admin_name,
        adminEmail: f.admin_email,
        adminPassword: f.admin_password,
        emailScheduled: result?.emailScheduled ?? false,
      });
      load();
    } catch (e: any) { setCreateError(e?.message || 'Failed to create tenant'); }
    finally { setCreateSaving(false); }
  };

  const openManage = async (t: Tenant) => {
    setResetPassword('');
    setResetResult(null);
    setManageTenant(t);
    setManageForm({
      name: t.name,
      email: t.email || '',
      phone: '',
      address: '',
      status: (t.status as typeof STATUSES[number]) || 'active',
      plan: (t.plan as typeof PLANS[number]) || 'hospital',
      max_users: t.max_users != null ? String(t.max_users) : '',
      max_clinics: t.max_clinics != null ? String(t.max_clinics) : '',
      license_expires_at: t.license_expires_at ? t.license_expires_at.slice(0, 10) : '',
    });
    setManageUsage(null);
    try {
      const full = await platformAdminGetTenant(t.id);
      if (full) {
        setManageForm(f => ({
          ...f,
          phone: full.phone || '',
          address: full.address || '',
        }));
        setManageUsage(full.usage || null);
      }
    } catch { /* keep defaults from list row */ }
  };

  const handleManageSave = async () => {
    if (!manageTenant) return;
    setManageSaving(true);
    try {
      await platformAdminUpdateTenant(manageTenant.id, {
        name: manageForm.name,
        email: manageForm.email || null,
        phone: manageForm.phone || null,
        address: manageForm.address || null,
        status: manageForm.status,
      });
      await platformAdminUpdateTenantLicense(manageTenant.id, {
        plan: manageForm.plan,
        max_users: manageForm.max_users ? Number(manageForm.max_users) : null,
        max_clinics: manageForm.max_clinics ? Number(manageForm.max_clinics) : null,
        license_expires_at: manageForm.license_expires_at || null,
      });
      ok('Tenant updated');
      setManageTenant(null);
      load();
    } catch (e: any) { err(e?.message || 'Failed to update tenant'); }
    finally { setManageSaving(false); }
  };

  const handleResetAdmin = async (resendEmail: boolean) => {
    if (!manageTenant || !resetPassword) return;
    setResetSaving(true);
    setResetResult(null);
    try {
      const result = await platformAdminResetTenantAdmin(manageTenant.id, {
        new_password: resetPassword,
        resend_email: resendEmail,
      });
      setResetResult({ adminEmail: result?.admin?.email ?? '', emailSent: result?.emailSent ?? false });
      setResetPassword('');
      ok('Admin password reset');
    } catch (e: any) { err(e?.message || 'Failed to reset admin password'); }
    finally { setResetSaving(false); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-purple-600" />
          <h1 className="text-lg font-semibold">Platform Admin — Tenants</h1>
        </div>
        <Button size="sm" onClick={() => { setCreateForm(EMPTY_CREATE_FORM); setCreateError(''); setCreateOpen(true); }}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Tenant
        </Button>
      </div>

      <Notify note={note} />

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : tenants.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center">No tenants yet.</div>
      ) : (
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 border-b">
                {['Tenant', 'Plan', 'Status', 'Expires', 'Users Limit', 'Clinics Limit', ''].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tenants.map((t, i) => (
                <tr key={t.id} className={cn('border-b last:border-0', i % 2 === 0 ? '' : 'bg-muted/10')}>
                  <td className="px-3 py-2.5">
                    <div className="font-medium flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-muted-foreground" />{t.name}</div>
                    <div className="text-xs text-muted-foreground">{t.email || t.subdomain || '—'}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium capitalize', PLAN_COLORS[t.plan] ?? 'bg-muted text-muted-foreground')}>{t.plan}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium capitalize', STATUS_COLORS[t.status] ?? 'bg-muted text-muted-foreground')}>{t.status}</span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">
                    {t.license_expires_at ? new Date(t.license_expires_at).toLocaleDateString() : 'No expiry'}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{t.max_users ?? 'Unlimited'}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{t.max_clinics ?? 'Unlimited'}</td>
                  <td className="px-3 py-2.5">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Manage" onClick={() => openManage(t)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create tenant */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Tenant</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tenant Name *"><Input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} /></Field>
              <Field label="MRN Prefix *">
                <Input value={createForm.mrn_prefix} maxLength={5} placeholder="e.g. ABC" onChange={e => setCreateForm(f => ({ ...f, mrn_prefix: e.target.value.toUpperCase() }))} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Subdomain"><Input value={createForm.subdomain} onChange={e => setCreateForm(f => ({ ...f, subdomain: e.target.value }))} /></Field>
              <Field label="Contact Email"><Input type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone"><Input value={createForm.phone} onChange={e => setCreateForm(f => ({ ...f, phone: e.target.value }))} /></Field>
              <Field label="Address"><Input value={createForm.address} onChange={e => setCreateForm(f => ({ ...f, address: e.target.value }))} /></Field>
            </div>

            <div className="pt-2 border-t">
              <p className="text-xs font-medium text-muted-foreground mb-2 mt-2">License</p>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Plan">
                  <Select value={createForm.plan} onValueChange={v => setCreateForm(f => ({ ...f, plan: v as typeof PLANS[number] }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PLANS.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Max Users"><Input type="number" min={0} placeholder="Unlimited" value={createForm.max_users} onChange={e => setCreateForm(f => ({ ...f, max_users: e.target.value }))} /></Field>
                <Field label="Max Clinics"><Input type="number" min={0} placeholder="Unlimited" value={createForm.max_clinics} onChange={e => setCreateForm(f => ({ ...f, max_clinics: e.target.value }))} /></Field>
              </div>
              <div className="mt-3">
                <Field label="Expires On"><Input type="date" value={createForm.license_expires_at} onChange={e => setCreateForm(f => ({ ...f, license_expires_at: e.target.value }))} /></Field>
              </div>
            </div>

            <div className="pt-2 border-t">
              <p className="text-xs font-medium text-muted-foreground mb-2 mt-2">First Admin User</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name *"><Input value={createForm.admin_name} onChange={e => setCreateForm(f => ({ ...f, admin_name: e.target.value }))} /></Field>
                <Field label="Email *"><Input type="email" value={createForm.admin_email} onChange={e => setCreateForm(f => ({ ...f, admin_email: e.target.value }))} /></Field>
              </div>
              <div className="mt-3">
                <Field label="Password *">
                  <div className="flex gap-2">
                    <Input type="text" placeholder="Min 6 characters" value={createForm.admin_password} onChange={e => setCreateForm(f => ({ ...f, admin_password: e.target.value }))} className="font-mono text-sm flex-1" />
                    <Button type="button" variant="outline" size="sm" className="shrink-0 text-xs px-2"
                      onClick={() => setCreateForm(f => ({ ...f, admin_password: generatePassword() }))}>
                      <RefreshCw className="w-3.5 h-3.5 mr-1" />Generate
                    </Button>
                  </div>
                </Field>
              </div>
            </div>

            <div className="pt-2 border-t">
              <div className="flex items-center gap-2 mt-2 mb-1">
                <Checkbox
                  id="admin_is_doctor"
                  checked={createForm.admin_is_doctor}
                  onCheckedChange={(c) => setCreateForm(f => ({ ...f, admin_is_doctor: c === true }))}
                />
                <Label htmlFor="admin_is_doctor" className="text-sm cursor-pointer flex items-center gap-1.5">
                  <Stethoscope className="w-3.5 h-3.5 text-muted-foreground" />
                  Admin is also a practicing doctor (solo practitioner)
                </Label>
              </div>
              {createForm.admin_is_doctor && (
                <div className="grid grid-cols-2 gap-3 mt-2 pl-6">
                  <Field label="Specialization">
                    <Input placeholder="e.g. General Physician" value={createForm.admin_specialty}
                      onChange={e => setCreateForm(f => ({ ...f, admin_specialty: e.target.value }))} />
                  </Field>
                  <Field label="Registration No.">
                    <Input placeholder="MCI / State Reg." value={createForm.admin_registration_no}
                      onChange={e => setCreateForm(f => ({ ...f, admin_registration_no: e.target.value }))} />
                  </Field>
                </div>
              )}
              {createForm.admin_is_doctor && (
                <p className="text-xs text-muted-foreground mt-2 pl-6">
                  A doctor profile will be created and linked to this admin account.
                  They will be able to prescribe and will appear in appointment / report filters.
                </p>
              )}
            </div>

            {createError && <div className="flex items-center gap-2 text-xs text-destructive"><AlertTriangle className="w-3.5 h-3.5" />{createError}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={createSaving}>Cancel</Button>
            <Button onClick={handleCreate} disabled={createSaving}>{createSaving ? 'Creating…' : 'Create Tenant'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials modal — shown after successful tenant creation */}
      <Dialog open={!!credentials} onOpenChange={v => { if (!v) setCredentials(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Tenant Created — Save Credentials
            </DialogTitle>
          </DialogHeader>
          {credentials && (
            <div className="space-y-4 py-1">
              <p className="text-sm text-muted-foreground">
                Share these login details with <strong>{credentials.adminName}</strong> at <strong>{credentials.tenantName}</strong>.
                The admin will be prompted to change their password on first login.
              </p>

              <div className="space-y-3 p-3 bg-muted/30 rounded-lg border">
                <CopyField label="Login Email" value={credentials.adminEmail} />
                <CopyField label="Temporary Password" value={credentials.adminPassword} secret />
                <CopyField label="App URL" value={window.location.origin} />
              </div>

              {credentials.emailScheduled ? (
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                  <Mail className="w-4 h-4 shrink-0" />
                  Welcome email with these credentials has been sent to {credentials.adminEmail}.
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  SMTP is not configured — email was not sent. Copy and share the credentials above manually.
                </div>
              )}

              <Button
                variant="outline"
                className="w-full text-xs"
                onClick={async () => {
                  const text = `Tenant: ${credentials.tenantName}\nLogin Email: ${credentials.adminEmail}\nTemporary Password: ${credentials.adminPassword}\nApp URL: ${window.location.origin}`;
                  await copyToClipboard(text);
                  ok('Credentials copied to clipboard');
                }}
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" />Copy All as Text
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCredentials(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage tenant */}
      <Dialog open={!!manageTenant} onOpenChange={v => { if (!v) setManageTenant(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Manage — {manageTenant?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            {manageUsage && (
              <div className="grid grid-cols-4 gap-2 text-center text-xs">
                <div className="rounded-md border p-2"><div className="text-base font-semibold">{manageUsage.users}</div>Users</div>
                <div className="rounded-md border p-2"><div className="text-base font-semibold">{manageUsage.clinics}</div>Clinics</div>
                <div className="rounded-md border p-2"><div className="text-base font-semibold">{manageUsage.doctors}</div>Doctors</div>
                <div className="rounded-md border p-2"><div className="text-base font-semibold">{manageUsage.patients}</div>Patients</div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Tenant Name"><Input value={manageForm.name} onChange={e => setManageForm(f => ({ ...f, name: e.target.value }))} /></Field>
              <Field label="Status">
                <Select value={manageForm.status} onValueChange={v => setManageForm(f => ({ ...f, status: v as typeof STATUSES[number] }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Contact Email"><Input type="email" value={manageForm.email} onChange={e => setManageForm(f => ({ ...f, email: e.target.value }))} /></Field>
              <Field label="Phone"><Input value={manageForm.phone} onChange={e => setManageForm(f => ({ ...f, phone: e.target.value }))} /></Field>
            </div>
            <Field label="Address"><Input value={manageForm.address} onChange={e => setManageForm(f => ({ ...f, address: e.target.value }))} /></Field>

            <div className="pt-2 border-t">
              <p className="text-xs font-medium text-muted-foreground mb-2 mt-2">License</p>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Plan">
                  <Select value={manageForm.plan} onValueChange={v => setManageForm(f => ({ ...f, plan: v as typeof PLANS[number] }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PLANS.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Max Users"><Input type="number" min={0} placeholder="Unlimited" value={manageForm.max_users} onChange={e => setManageForm(f => ({ ...f, max_users: e.target.value }))} /></Field>
                <Field label="Max Clinics"><Input type="number" min={0} placeholder="Unlimited" value={manageForm.max_clinics} onChange={e => setManageForm(f => ({ ...f, max_clinics: e.target.value }))} /></Field>
              </div>
              <div className="mt-3">
                <Field label="Expires On"><Input type="date" value={manageForm.license_expires_at} onChange={e => setManageForm(f => ({ ...f, license_expires_at: e.target.value }))} /></Field>
              </div>
            </div>
          </div>
          <div className="pt-2 border-t">
            <p className="text-xs font-medium text-muted-foreground mb-2 mt-2">Reset Admin Password</p>
            <div className="flex gap-2">
              <div className="flex-1 flex gap-2">
                <Input
                  type="text"
                  placeholder="New password (min 6 chars)"
                  value={resetPassword}
                  onChange={e => setResetPassword(e.target.value)}
                  className="font-mono text-sm flex-1"
                />
                <Button type="button" variant="outline" size="sm" className="shrink-0 text-xs px-2"
                  onClick={() => setResetPassword(generatePassword())}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />Gen
                </Button>
              </div>
            </div>
            <div className="flex gap-2 mt-2">
              <Button
                variant="outline" size="sm" className="flex-1 text-xs"
                disabled={resetSaving || resetPassword.length < 6}
                onClick={() => handleResetAdmin(false)}
              >
                Reset Password
              </Button>
              <Button
                variant="outline" size="sm" className="flex-1 text-xs"
                disabled={resetSaving || resetPassword.length < 6}
                onClick={() => handleResetAdmin(true)}
              >
                <Mail className="w-3.5 h-3.5 mr-1" />Reset &amp; Send Email
              </Button>
            </div>
            {resetResult && (
              <div className={cn('mt-2 flex items-center gap-2 text-xs rounded px-2 py-1.5',
                resetResult.emailSent ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
              )}>
                {resetResult.emailSent
                  ? <><CheckCircle className="w-3.5 h-3.5" />Password reset and welcome email sent to {resetResult.adminEmail}.</>
                  : <><AlertTriangle className="w-3.5 h-3.5" />Password reset. Email not sent (SMTP not configured) — share credentials manually.</>}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setManageTenant(null)} disabled={manageSaving}>Cancel</Button>
            <Button onClick={handleManageSave} disabled={manageSaving}>{manageSaving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
